/**
 * Metal ramp for service awards, kept out of the component file so it can be
 * imported by tests and by anything that needs the mapping without pulling in JSX.
 *
 * Deliberately keyed off YEARS directly, not off the coarser SERVICE_AWARDS rank
 * tier (see serviceAwards.js). The rank ladder still names the era ("Veteran",
 * "Luminary"...); the metal is a finer-grained fact that keeps changing within a
 * single rank, so a 20-year and a 24-year Veteran can render a visibly different
 * crest even though their rank name doesn't change until 25.
 *
 * Seven solid metals ascend on one axis, warmth and value: bronze → silver → gold
 * → onyx → platinum → obsidian → diamond. Between each pair of solids sits a
 * two-tone transition stop (e.g. "bronze/silver") so the ramp reads as continuous
 * growth rather than a stack of six discrete categories. The crest renders a
 * transition stop as a dual ring: the plate and inner hairline are the `base`
 * metal, the outer burnished rim is the `accent` metal.
 */

const RAMP = [
  { years: 0, base: 'bronze' },
  { years: 4, base: 'bronze', accent: 'silver' },
  { years: 6, base: 'silver' },
  { years: 10, base: 'silver', accent: 'gold' },
  { years: 12, base: 'gold' },
  { years: 16, base: 'gold', accent: 'onyx' },
  { years: 18, base: 'onyx' },
  { years: 22, base: 'onyx', accent: 'platinum' },
  { years: 24, base: 'platinum' },
  { years: 28, base: 'platinum', accent: 'obsidian' },
  { years: 30, base: 'obsidian' },
  { years: 34, base: 'obsidian', accent: 'diamond' },
  { years: 36, base: 'diamond' },
];

/**
 * @param {number} years
 * @returns {{ base: string, accent?: string }}
 */
export function metalForYears(years) {
  const y = Number.isFinite(years) && years > 0 ? years : 0;
  let stop = RAMP[0];
  for (const candidate of RAMP) {
    if (y < candidate.years) break;
    stop = candidate;
  }
  return stop.accent ? { base: stop.base, accent: stop.accent } : { base: stop.base };
}
